import {
  createContext,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { getSelectedData, getSentences } from '../../screens/SentenceSet/api';
import { getTags } from '../../screens/WordSet/api';
import {
  ARRAY_STATE,
  CHANGE_PAGE,
  OUTER_STATE,
  SENTENCE_STATE,
  SUBJECT_STATE,
} from './constants';
import { logError } from '@/utils/logHandling';
import { SentenceType, SubjectType } from '@/screens/SentenceSet/config';

export type SentenceState = {
  sentenceSubTypes: string[];
  term: string;
  termType: string;
  sentenceType: SentenceType;
  subjectType: SubjectType;
  searchSentencesPerPage: number;
  page: number;
  data?: any;
  selectedData?: any;
  limitModal?: any;
  editData?: any;
  handleOuterState?: any;
  handleArrayState?: any;
  handleSentenceTypeState?: any;
  handleSubjectTypeState?: any;
  changeData?: any;
  changeSelectedData?: any;
  changePage?: any;
  changeLimitModal?: any;
  refetchSentences?: any;
  patientId?: any;
  setId?: any;
  apiName?: any;
  scrollViewRef?: any;
  tags?: any;
};

const INITIAL_STATE: SentenceState = {
  sentenceSubTypes: ['standard', 'particleVerb'],
  term: '',
  termType: 'sentence',
  sentenceType: SentenceType.OV,
  subjectType: SubjectType.NOMEN,
  searchSentencesPerPage: 30,
  page: 1,
};
export const SentenceSetContext = createContext(INITIAL_STATE);

const reducer = (state = INITIAL_STATE, action) => {
  const { type, payload } = action;

  switch (type) {
    case OUTER_STATE: {
      const { name, value } = payload;
      return {
        ...state,
        page: 1,
        [name]: value,
      };
    }

    case SENTENCE_STATE: {
      const { value, sentenceTypeArray } = payload;

      return {
        ...state,
        page: 1,
        sentenceType: value,
        sentenceSubTypes: sentenceTypeArray,
      };
    }
    case SUBJECT_STATE: {
      const { value, sentenceTypeArray } = payload;

      return {
        ...state,
        page: 1,
        sentenceSubTypes: sentenceTypeArray,
        subjectType: value,
      };
    }

    case CHANGE_PAGE: {
      const { value, arrowName } = payload;
      if (arrowName === null) {
        return {
          ...state,
          page: value,
        };
      } else {
        if (arrowName === 'right') {
          return {
            ...state,
            page: state.page + 1,
          };
        } else {
          return {
            ...state,
            page: state.page - 1,
          };
        }
      }
    }

    case ARRAY_STATE: {
      const { value, isRemove } = payload;
      if (!isRemove) {
        return {
          ...state,
          page: 1,
          sentenceSubTypes: [...state.sentenceSubTypes, value],
        };
      } else {
        const newSentenceSubTypes = state.sentenceSubTypes.filter(
          (eachType) => {
            return eachType !== value;
          },
        );
        return {
          ...state,
          page: 1,
          sentenceSubTypes: newSentenceSubTypes,
        };
      }
    }
    default:
      return state;
  }
};

interface SentenceSetContextProviderProps {
  children: React.ReactElement;
  patientId: string;
  setId: string;
  apiName: string;
}
export function SentenceSetContextProvider({
  children,
  patientId,
  setId,
  apiName,
}: SentenceSetContextProviderProps) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const [tags, setTags] = useState(null);
  // on pagination press, we want to scroll up in the sets
  const scrollViewRef = useRef(null);

  const [data, setData] = useState({
    sentences: [],
    totalCount: 0,
  });
  const [limitModal, setLimitModal] = useState(false);
  const [selectedData, setSelectedData] = useState([]);
  const componentMounted = useRef(false);
  const requestRef = useRef(null);
  const editData = useRef(null);

  const changePage = useCallback((value: number, arrowName = null) => {
    dispatch({
      type: CHANGE_PAGE,
      payload: { value, arrowName },
    });
    if (scrollViewRef.current && value) {
      scrollViewRef.current.scrollTo({ y: 150, animated: true });
    }
  }, []);

  const handleOuterState = useCallback((name: string, value: string) => {
    dispatch({
      type: OUTER_STATE,
      payload: { name, value },
    });
  }, []);

  const handleSentenceTypeState = useCallback(
    (value: SentenceType, sentenceTypeArray: string[]) => {
      dispatch({
        type: SENTENCE_STATE,
        payload: { value, sentenceTypeArray },
      });
    },
    [],
  );

  const handleSubjectTypeState = useCallback(
    (value: SubjectType, sentenceTypeArray: string[]) => {
      dispatch({
        type: SUBJECT_STATE,
        payload: { value, sentenceTypeArray },
      });
    },
    [],
  );

  const handleArrayState = useCallback((value: string[], isRemove: boolean) => {
    dispatch({
      type: ARRAY_STATE,
      payload: { value, isRemove },
    });
  }, []);

  const changeLimitModal = useCallback(() => {
    setLimitModal((prevState) => !prevState);
  }, []);

  const changeSelectedData = useCallback((newSelectedData) => {
    setSelectedData(newSelectedData);
  }, []);

  const changeData = useCallback((newData) => {
    setData(newData);
  }, []);

  const handleFetchSentences = useCallback(async (object) => {
    try {
      let payload;
      if (object?.termType === 'sentence' || object?.termType === 'tag') {
        payload = { ...object, term: object?.term.trim() };
      } else {
        payload = { ...object };
      }

      const { data } = await getSentences({
        ...payload,
        sentenceSubTypes: payload.sentenceSubTypes,
        subjectType:
          object.sentenceType === 'OV' || object.sentenceType === 'VO'
            ? undefined
            : payload.subjectType,
      });
      setData(data);
    } catch (error) {
      logError('Could not fetch sentences', error);
    }
  }, []);

  const handleFetchTags = useCallback(async () => {
    try {
      const { data } = await getTags();
      const { sentences } = data;
      setTags({ sentences });
    } catch (error) {
      logError('Could not fetch tags', error);
    }
  }, []);

  const handleFetchSelectedSentences = useCallback(
    async (apiName, patientId, setId) => {
      try {
        const { data } = await getSelectedData(apiName, patientId, setId);
        editData.current = data;
        const { sentences } = data;
        setSelectedData(sentences);
      } catch (error) {
        logError('Could not fetch selected sentences', error);
      }
    },
    [],
  );

  const refetchSentences = useCallback(() => {
    handleFetchSentences(state);
  }, [state]);

  useEffect(() => {
    if (componentMounted.current && state.page != '' && state.page != '0') {
      clearTimeout(requestRef.current);
      requestRef.current = setTimeout(() => {
        handleFetchSentences(state);
      }, 500);
    }
  }, [state.term]);

  useEffect(() => {
    if (componentMounted.current && state.page != '' && state.page != '0') {
      handleFetchSentences(state);
    }
  }, [
    state.sentenceType,
    state.subjectType,
    state.sentenceSubTypes,
    state.page,
    state.termType,
  ]);

  useEffect(() => {
    const fetchedSentences = async () => {
      if (!setId) {
        handleFetchSentences(state);
        handleFetchTags();
      } else {
        try {
          handleFetchTags();
          handleFetchSentences(state);
          handleFetchSelectedSentences(apiName, patientId, setId);
        } catch (error) {
          logError('Could not fetch', error);
        }
      }
      componentMounted.current = true;
    };
    fetchedSentences();
  }, []);

  const value = {
    data,
    selectedData,
    limitModal,
    editData: editData.current,
    page: state.page,
    searchSentencesPerPage: state.searchSentencesPerPage,
    term: state.term,
    termType: state.termType,
    sentenceType: state.sentenceType,
    subjectType: state.subjectType,
    sentenceSubTypes: state.sentenceSubTypes,
    handleOuterState,
    handleArrayState,
    handleSentenceTypeState,
    handleSubjectTypeState,
    changeData,
    changeSelectedData,
    changePage,
    changeLimitModal,
    refetchSentences,
    patientId,
    setId,
    apiName,
    scrollViewRef,
    tags:
      tags?.sentences?.map((item) => {
        return item.hierarchy;
      }) ?? [],
  };

  return (
    <SentenceSetContext.Provider value={value}>
      {children}
    </SentenceSetContext.Provider>
  );
}
